import { ActionTypes, PUBLIC_PATH } from 'constants/index';
import jwtService from 'jwtService';
import { AuthService, OrgService, AppService } from 'modules/api/index';
import { toast } from 'react-toastify';
import { call, put, takeEvery } from 'redux-saga/effects';
import { history } from 'dsilo-ui-components';

async function fetchAsync(func, arg) {
	const response = await func(arg);
	return response;
}

/**
 * API layers in this app reject with different shapes: a ServerError, the parsed
 * response body, or a plain string. Pick the first usable message out of them.
 */
function getApiErrorMessage(error, fallbackMessage) {
	if (typeof error === 'string' && error.trim()) {
		return error;
	}
	// fetch rejects with a TypeError when the server is unreachable
	if (error?.name === 'TypeError') {
		return 'Unable to reach the server. Please check your connection and try again.';
	}
	const candidates = [
		error?.message,
		error?.error?.message,
		error?.response?.message,
		error?.response?.data?.message,
		error?.response?.error,
		error?.error,
	];
	return candidates.find(candidate => typeof candidate === 'string' && candidate.trim()) || fallbackMessage;
}

/** True only for values that actually carry failure information. */
function hasErrorContent(value) {
	if (typeof value === 'boolean') {
		return value;
	}
	if (typeof value === 'number') {
		return value !== 0;
	}
	if (typeof value === 'string') {
		return !['', 'false', 'null', 'undefined', '0'].includes(value.trim().toLowerCase());
	}
	if (Array.isArray(value)) {
		return value.length > 0;
	}
	if (value && typeof value === 'object') {
		return Object.keys(value).length > 0;
	}
	return false;
}

/**
 * A 2xx response can still carry a failure payload, but an empty or placeholder
 * `error` key on an otherwise successful envelope is not a failure.
 */
function getUnsuccessfulPayloadMessage(result) {
	if (result === null || result === undefined) {
		return 'Something went wrong. Please try again.';
	}
	if (typeof result !== 'object') {
		return '';
	}
	const failed =
		result.success === false ||
		['error', 'failed', 'failure'].includes(status) ||
		(typeof result.statusCode === 'number' && result.statusCode >= 400) ||
		hasErrorContent(result.error);
	return failed ? getApiErrorMessage(result, 'Something went wrong. Please try again.') : '';
}

function* fetchAppLogin(action) {
	try {
		let user = yield call(fetchAsync, jwtService.signInWithEmailAndPassword, action.payload);
		yield put({ type: ActionTypes.LOGIN_SUCCESS, payload: user.data });
		yield put({ type: ActionTypes.SET_USER_DATA, payload: user.data });
		history.push(`/apploading`);
	} catch (e) {
		yield put({ type: ActionTypes.LOGIN_ERROR, error: e });
	}
}

function* checkEmailAad(action) {
	try {
		let response = yield call(fetchAsync, jwtService.checkEmailForAad, action.payload);
		yield put({ type: ActionTypes.CHECK_EMAIL_AAD_SUCCESS, payload: response.data });
	} catch (e) {
		yield put({ type: ActionTypes.CHECK_EMAIL_AAD_ERROR, error: e });
	}
}

function* checkEmailLLSE(action) {
	console.log("checkEmailLLSE ===== saga", action.payload);
	try {
		let response = yield call(fetchAsync, jwtService.checkEmailForLLSE, action.payload);
		yield put({ type: ActionTypes.CHECK_EMAIL_AAD_SUCCESS, payload: response.data });
	} catch (e) {
		yield put({ type: ActionTypes.CHECK_EMAIL_AAD_ERROR, error: e });
	}
}

function* AppverifyEmail(action) {
	try {
		const emailResult = yield call(fetchAsync, jwtService.verifyEmail, action.payload);
		yield put({ type: ActionTypes.VERIFY_EMAIL_SUCCESS, payload: emailResult });
	} catch (e) {
		yield put({ type: ActionTypes.VERIFY_EMAIL_ERROR, error: e.message });
	}
}

function* resetPassword(action) {
	try {
		const result = yield call(fetchAsync, jwtService.resetPassword, action.payload.data);
		action.payload.logoutUser()
		yield put({ type: ActionTypes.RESET_PASSWORD_SUCCESS, payload: result });
		toast.info(result?.message || 'Password reset successfully')
		history.push(`/${PUBLIC_PATH}login`);
	} catch (e) {
		yield put({
			type: ActionTypes.RESET_PASSWORD_ERROR,
			error: getApiErrorMessage(e, 'Unable to reset the password. Please try again.'),
		});
	}
}

function* changePassword(action) {
	try {
		const result = yield call(fetchAsync, jwtService.changePassword, action.payload);
		yield put({ type: ActionTypes.CHANGE_PASSWORD_SUCCESS, payload: result });

		// action.payload.logoutUser()
		// toast.info(result?.message || 'Password reset successfully')
		// history.push(`/${PUBLIC_PATH}login`);
	} catch (e) {
		console.log('error in change password', e)
		const errorMessage = e?.message || e?.response?.message || e?.response?.data?.message || "Something went wrong"
		toast.error(errorMessage)
		yield put({ type: ActionTypes.CHANGE_PASSWORD_FAILURE, error: e.message });
	}
}

function* AppforgotPassword(action) {
	const fallbackMessage = 'Unable to send the reset link. Please try again.';
	try {
		const result = yield call(fetchAsync, AuthService.forgotPassword, action.payload);
		const payloadError = getUnsuccessfulPayloadMessage(result);
		if (payloadError) {
			yield put({ type: ActionTypes.FORGOT_PASSWORD_ERROR, error: payloadError });
			return;
		}
		yield put({ type: ActionTypes.FORGOT_PASSWORD_SUCCESS, payload: result });
	} catch (e) {
		yield put({ type: ActionTypes.FORGOT_PASSWORD_ERROR, error: getApiErrorMessage(e, fallbackMessage) });
	}
}
function* verifyUser(action) {
	try {
		const result = yield call(fetchAsync, jwtService.verifyUser, action.payload);
		yield put({ type: ActionTypes.VERIFY_USER_SUCCESS, payload: result });
	} catch (e) {
		yield put({ type: ActionTypes.VERIFY_USER_ERROR, error: e.message });
	}
}
function* resendVerifyEmail(action) {
	try {
		const result = yield call(fetchAsync, AuthService.resendVerifyEmail, action.payload);
		yield put({ type: ActionTypes.RESEND_VERIFY_EMAIL_SUCCESS, payload: result });
	} catch (e) {
		yield put({ type: ActionTypes.RESEND_VERIFY_EMAIL_ERROR, error: e.message });
	}
}
function* updateProfile(action) {
	const { user, userName } = action.payload;
	try {
		const result = yield call(fetchAsync, AuthService.updateProfile, action.payload);
		yield put({ type: ActionTypes.UPDATE_PROFILE_SUCCESS, payload: result });
		const updatedUser = { ...user, userName: userName }
		yield put({ type: ActionTypes.SET_USER_DATA, payload: updatedUser });
	} catch (e) {
		yield put({ type: ActionTypes.UPDATE_PROFILE_ERROR, error: e.message });
	}
}

export function* watchLoginSaga() {
	yield takeEvery(ActionTypes.CHECK_EMAIL_AAD, checkEmailAad);
	yield takeEvery(ActionTypes.CHECK_EMAIL_LLSE, checkEmailLLSE);
	yield takeEvery(ActionTypes.LOGIN_LOADING, fetchAppLogin);
	yield takeEvery(ActionTypes.RESET_PASSWORD_LOADING, resetPassword);
	yield takeEvery(ActionTypes.CHANGE_PASSWORD_LOADING, changePassword);
	yield takeEvery(ActionTypes.VERIFY_EMAIL_LOADING, AppverifyEmail);
	yield takeEvery(ActionTypes.FORGOT_PASSWORD_LOADING, AppforgotPassword);
	yield takeEvery(ActionTypes.VERIFY_USER_LOADING, verifyUser);
	yield takeEvery(ActionTypes.RESEND_VERIFY_EMAIL_LOADING, resendVerifyEmail);
	yield takeEvery(ActionTypes.UPDATE_PROFILE_LOADING, updateProfile);
}

export default watchLoginSaga;
